import React, { useState, useEffect } from "react";
import { ShieldCheck } from "lucide-react";
import { useAuth } from "../../lib/AuthContext";
import { useAdminMode } from "../../lib/AdminModeContext";

// Inline admin pattern: the TOGGLE pill lives in the page header (top-right),
// the revealed PANEL renders full-width directly under the header.

export function useInlineAdmin() {
    const { isAdmin } = useAuth();
    const { adminMode, ownerView } = useAdminMode();
    const [open, setOpen] = useState(false);
    // Follow global Admin Mode OR the Owner button (either one unlocks editing, e.g. lineups).
    // A manual toggle still works until either mode next changes.
    useEffect(() => { setOpen(adminMode || ownerView); }, [adminMode, ownerView]);
    return { isAdmin, open, setOpen, toggle: () => setOpen((v) => !v) };
}

// Per-page admin toggle pills have been removed — admin tools are revealed via
// the global Admin Mode toggle (top-nav on desktop, bottom-nav on mobile).
export function InlineAdminToggle() {
    return null;
}

// Revealed panel — place directly under the header (full width).
export function InlineAdminPanel({ open, testId, children }) {
    if (!open) return null;
    return (
        <div className="mb-6 rounded-xl bg-[#12141D] border border-[#9D4CDD]/40 p-4" data-testid={`${testId}-panel`}>
            {children}
        </div>
    );
}

// Convenience block (toggle + panel together). Used by Batch A spots where the
// admin tools sit beneath a tab strip rather than a page header.
export function InlineAdminSection({ label = "Admin Tools", testId = "inline-admin", children }) {
    const { isAdmin, open, toggle } = useInlineAdmin();
    if (!isAdmin) return null;
    return (
        <div className="my-4" data-testid={testId}>
            <InlineAdminToggle open={open} onClick={toggle} label={label} testId={testId} />
            <div className="mt-3">
                <InlineAdminPanel open={open} testId={testId}>{children}</InlineAdminPanel>
            </div>
        </div>
    );
}

// Internal sub-tab switcher for grouped admin sections (e.g. Draft group).
export function AdminSubTabs({ tabs, value, onChange, testId = "admin-subtabs" }) {
    return (
        <div className="inline-flex rounded-md border border-[#1E2233] overflow-hidden bg-[#0A0B10] mb-4" data-testid={testId}>
            {tabs.map((t) => (
                <button key={t.k} onClick={() => onChange(t.k)} data-testid={`${testId}-${t.k}`}
                    className={`px-4 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${value === t.k ? "bg-[#9D4CDD] text-white" : "text-[#94A3B8] hover:text-white"}`}>
                    {t.l}
                </button>
            ))}
        </div>
    );
}
